import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { signInWithRedirect } from 'aws-amplify/auth';
import ErrorMessage from '../../ErrorMessage';
import { EyeIcon, EyeOffIcon, GoogleIcon, LockIcon, MailIcon } from '../../../assets/icons';
import { useLoginContext } from '../LoginContext';

const isGoogleEnabled = Boolean(process.env.REACT_APP_COGNITO_DOMAIN);

function Step1({ authenticateSubmit }) {
  const { email, setEmail, password, setPassword, error, setError } = useLoginContext();
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const clearErrorOnChange = (setter) => (e) => {
    if (error) setError(undefined);
    setter(e.target.value);
  };

  return (
    <div className='w-full max-w-[540px] overflow-hidden rounded-[20px] bg-white px-6 py-9 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
      <div className='text-center'>
        <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#0F2540]'>
          <LockIcon className='h-7 w-7' strokeWidth='1.75' />
        </div>
        <h3 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Welcome Back</h3>
        <p className='mt-2 text-sm leading-6 text-[#7A9AB0]'>Sign in to your account to continue</p>
      </div>
      <form className='mt-7 space-y-4' onSubmit={authenticateSubmit}>
        <input type='text' autoComplete='username' style={{ display: 'none' }} tabIndex={-1} aria-hidden='true' />
        <div className='space-y-1.5'>
          <label htmlFor='formBasicEmail' className='block text-[11.5px] font-semibold text-[#7A9AB0]'>
            Email address
          </label>
          <div className='relative'>
            <MailIcon className='absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2E6BAD]' />
            <input
              ref={emailInputRef}
              id='formBasicEmail'
              className='flex h-12 w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 pl-10 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10'
              type='email'
              value={email}
              onChange={clearErrorOnChange(setEmail)}
              autoComplete='username'
              placeholder='you@example.com'
            />
          </div>
        </div>
        <div className='space-y-1.5'>
          <label htmlFor='formBasicPassword' className='block text-[11.5px] font-semibold text-[#7A9AB0]'>
            Password
          </label>
          <div className='relative'>
            <input
              id='formBasicPassword'
              className='flex h-12 w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 pr-11 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10'
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={clearErrorOnChange(setPassword)}
              autoComplete='current-password'
              placeholder='Enter your password'
            />
            <button
              type='button'
              className='absolute right-3 top-1/2 -translate-y-1/2 text-[#7A9AB0] transition hover:text-[#2E6BAD] focus:outline-none'
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon className='w-4 h-4' /> : <EyeIcon className='w-4 h-4' />}
            </button>
          </div>
        </div>
        <a href='/forgot-password' className='block text-center text-sm font-medium text-[#2E6BAD] hover:underline'>
          Forgot Password?
        </a>
        {isGoogleEnabled && (
          <>
            <div className='flex justify-center'>
              <button
                type='button'
                onClick={() => signInWithRedirect({ provider: 'Google' })}
                className='flex h-12 w-full items-center justify-center gap-2 rounded-full border-[1.5px] border-[#0B1929]/15 bg-transparent px-4 text-sm font-semibold text-[#3D5A73] transition hover:bg-[#EBF4FB]'
              >
                <GoogleIcon className='h-5 w-5' />
                Sign in with Google
              </button>
            </div>
            <p className='text-center text-sm text-[#7A9AB0]'>or</p>
          </>
        )}
        <button
          className='inline-flex h-12 w-full items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9]'
          type='submit'
          disabled={!email || password === ''}
        >
          Login
        </button>
        {error && <ErrorMessage error={error} className='form-error-display' />}
        <div className='flex items-center justify-center gap-2 pt-1'>
          <p className='text-sm text-[#7A9AB0]'>Don&apos;t have an account?</p>
          <a href='/register' className='text-sm font-medium text-[#2E6BAD] hover:underline'>
            Sign up
          </a>
        </div>
      </form>
    </div>
  );
}

Step1.propTypes = {
  authenticateSubmit: PropTypes.func.isRequired,
};

export default Step1;
